import { ApolloClient, DocumentNode } from '@apollo/client';
import {
  VehicleModel,
  GetAllModelsQueryResult,
  GetQSGByYearAndModelQueryResult,
  GetQSGNavigationQueryResult,
  GetSectionByPathQueryResult,
  NavigationGuide,
  QsgV1Model,
  SectionModel,
  DisclaimerFeatureModel,
  GetAllDisclaimersQueryResult,
  GetQSGSearchResultsQueryResult,
  GetQsgSearchResultsResponse,
} from './types';
import {
  GET_ALL_DISCLAIMERS,
  GET_ALL_MODELS,
  GET_QSG_BY_YEAR_AND_MODEL,
  GET_QSG_NAVIGATION,
  GET_QSG_SEARCH_RESULTS,
  GET_SECTION_BY_PATH,
} from './queries';
import { GET_ALL_MODELS_MOCK } from './mock/get-all-models';
import { ENABLE_MOCKING, USE_PUBLISH_ENDPOINT } from './config';
import { GET_QSG_BY_YEAR_AND_MODEL_MOCK } from './mock/get-qsg-by-year-and-model';
import {
  aemGetAllDisclaimers,
  aemGetAllModels,
  aemGetQSGByYearAndModel,
  aemGetQSGNavigation,
  aemGetSectionByPath,
} from './aem/helpers';
import { isValidNavigation } from '../../utils/navigation';

type QueryOptions<
  TData = any,
  TVariables extends Record<string, any> = Record<string, any>,
> = {
  query: DocumentNode;
  variables?: TVariables;
  dataKey?: keyof TData;
  client: ApolloClient<any>;
  errorKey?: string;
  fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache';
  timeout?: number; // Timeout in milliseconds, default 15000 (15 seconds)
};

export async function runQuery<
  TData = any,
  TVariables extends Record<string, any> = Record<string, any>,
>({
  query,
  variables,
  dataKey,
  client,
  errorKey,
  fetchPolicy = 'network-only',
  timeout = 60000,
}: QueryOptions<TData, TVariables>): Promise<any> {
  try {
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Query timeout after ${timeout}ms`));
      }, timeout);
    });

    const { data, errors } = await Promise.race([
      client.query<TData, TVariables>({
        query,
        variables,
        fetchPolicy,
      }),
      timeoutPromise,
    ]);

    if (errors && errors.length > 0) {
      throw new Error(errors.map((e) => e.message).join(', '));
    }

    if (dataKey) {
      const result = (data as any)?.[dataKey];
      if (!result) throw new Error(`Missing data for key: ${String(dataKey)}`);
      return result;
    }

    return data;
  } catch (err: any) {
    console.log('err:', err);
    const message = errorKey
      ? (CUSTOM_ERRORS?.[errorKey] ?? err.message ?? '')
      : '';
    throw new Error(message || 'GraphQL query failed');
  }
}

export const getAvailableModels = async (
  client: ApolloClient<any>
): Promise<VehicleModel[]> => {
  if (USE_PUBLISH_ENDPOINT) {
    const allModels = await aemGetAllModels();
    return allModels.models;
  }

  if (ENABLE_MOCKING) {
    await sleep(1);
    return GET_ALL_MODELS_MOCK.models;
  }

  const data = await runQuery<GetAllModelsQueryResult>({
    query: GET_ALL_MODELS,
    dataKey: 'getAllQsgModels',
    fetchPolicy: 'network-only',
    client,
    errorKey: 'getAvailableModels',
  });

  return data.models;
};

export const getQSGContent = async (
  modelId: string,
  modelYear: string,
  client: ApolloClient<any>
): Promise<QsgV1Model | undefined> => {
  if (USE_PUBLISH_ENDPOINT) {
    const guide = await aemGetQSGByYearAndModel(modelYear, modelId);
    return guide.guides[0];
  }

  if (ENABLE_MOCKING) {
    const mockGuide: any = GET_QSG_BY_YEAR_AND_MODEL_MOCK.guides[0];
    await sleep(1);
    if (mockGuide.modelId !== modelId || mockGuide.modelYear !== modelYear)
      return;
    if (!mockGuide || mockGuide.sections.length === 0) return;
    return mockGuide;
  }

  const data = await runQuery<GetQSGByYearAndModelQueryResult>({
    query: GET_QSG_BY_YEAR_AND_MODEL,
    dataKey: 'getQsgByYearAndModel',
    variables: {
      year: modelYear,
      modelId,
    },
    fetchPolicy: 'network-only',
    client,
    errorKey: 'getQSGContent',
  });

  const guide = data.guides[0];

  if (!guide) return;

  return guide;
};

export const getQSGNavigation = async (
  modelId: string,
  modelYear: string,
  client: ApolloClient<any>
): Promise<NavigationGuide | undefined> => {
  if (USE_PUBLISH_ENDPOINT) {
    const data = await aemGetQSGNavigation(modelYear, modelId);
    if (!isValidNavigation(data.navigation[0])) return;
    return data.navigation[0];
  }

  const data = await runQuery<GetQSGNavigationQueryResult>({
    query: GET_QSG_NAVIGATION,
    dataKey: 'getQsgNavigation',
    variables: {
      year: modelYear,
      modelId,
    },
    fetchPolicy: 'network-only',
    client,
    errorKey: 'getQSGNavigation',
  });

  const navigationGuide = data.navigation[0];

  if (!isValidNavigation(navigationGuide)) return;
  return navigationGuide;
};

export const getSectionByPath = async (
  path: string,
  client: ApolloClient<any>
): Promise<SectionModel | undefined> => {
  if (USE_PUBLISH_ENDPOINT) {
    const data = await aemGetSectionByPath(path);
    return data.section;
  }

  const data = await runQuery<GetSectionByPathQueryResult>({
    query: GET_SECTION_BY_PATH,
    dataKey: 'getQsgSectionByPath',
    variables: {
      path,
    },
    fetchPolicy: 'network-only',
    client,
    errorKey: 'getSectionByPath',
  });

  const section = data.section;

  return section;
};

export const getQSGSearchResults = async (
  year: string,
  modelId: string,
  searchTerm: string,
  page: number,
  limit: number,
  client: ApolloClient<any>
): Promise<GetQsgSearchResultsResponse | undefined> => {
  const data = await runQuery<GetQSGSearchResultsQueryResult>({
    query: GET_QSG_SEARCH_RESULTS,
    dataKey: 'getQsgSearchResults',
    variables: {
      year,
      modelId,
      searchTerm,
      page,
      limit,
    },
    fetchPolicy: 'network-only',
    client,
    errorKey: 'getQSGSearchResults',
  });

  return data;
};

export const getAllDisclaimers = async (
  client: ApolloClient<any>
): Promise<DisclaimerFeatureModel[]> => {
  if (USE_PUBLISH_ENDPOINT) {
    const data = await aemGetAllDisclaimers();
    return data.disclaimers;
  }

  const data = await runQuery<GetAllDisclaimersQueryResult>({
    query: GET_ALL_DISCLAIMERS,
    dataKey: 'getAllQsgDisclaimers',
    fetchPolicy: 'network-only',
    client,
    errorKey: 'getAllDisclaimers',
  });

  return data.disclaimers;
};

export const castQSGContentToNavigation = (
  content: QsgV1Model
): NavigationGuide => {
  return content as unknown as NavigationGuide;
};

export const aggregateSectionMap = (
  content: QsgV1Model
): Record<string, SectionModel> | undefined => {
  // Recursive function to add section and all nested subsections to the map
  const addSectionToMap = (
    section: SectionModel,
    map: Record<string, SectionModel>
  ) => {
    // Add the current section to the map
    map[section.sectionId] = section;

    // Recursively add all nested subsections
    if (section.subsections) {
      section.subsections.forEach((subsection) => {
        addSectionToMap(subsection, map);
      });
    }
  };

  const sectionMap = content.sections?.reduce(
    (acc, section) => {
      addSectionToMap(section, acc);
      return acc;
    },
    {} as Record<string, SectionModel>
  );

  return sectionMap;
};

export const sleep = (s: number) =>
  new Promise((resolve) => setTimeout(resolve, s * 1000));

/**
 * Fetches a PDF from the backend API
 * @param graphQLServerUrl - The GraphQL server URL
 * @param modelYear - The model year
 * @param modelId - The model ID
 * @returns Promise with the PDF blob and filename
 */
export const fetchPDF = async (
  graphQLServerUrl: string,
  modelYear: string,
  modelId: string
): Promise<{ blob: Blob; filename: string }> => {
  const baseUrl = graphQLServerUrl.replace('/graphql', '');
  const pdfUrl = `${baseUrl}/api/pdf/download?year=${modelYear}&modelId=${modelId}`;

  const response = await fetch(pdfUrl, {
    method: 'GET',
    headers: {
      Accept: 'application/pdf',
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to download PDF: ${response.statusText}`);
  }

  // Get the filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = `quick-start-guide-${modelId}-${modelYear}.pdf`;

  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+?)"?$/);
    if (filenameMatch && filenameMatch[1]) {
      filename = filenameMatch[1];
    }
  }

  const blob = await response.blob();

  return { blob, filename };
};

const CUSTOM_ERRORS = {
  getAvailableModels: 'Error fetching available models',
  getQSGContent: 'Error fetching QSG content',
  getQSGNavigation: 'Error fetching QSG navigation',
  getSectionByPath: 'Error fetching section',
  getAllDisclaimers: 'Error fetching disclaimers',
  getQSGSearchResults: 'Error fetching QSG search results',
};
